import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import classes from './HirablePopover.module.css';

const POPOVER_WIDTH = 220;
// Conservative estimate of the popover's own height, used only to decide
// above-vs-below placement before the real node has ever been measured —
// same "estimate before mount, position once" approach SearchAndCategories'
// old category options popover used.
const ESTIMATED_HEIGHT = 110;
const GAP = 8;
const VIEWPORT_MARGIN = 8;

// Shared "Available to hire" popover for the Hirable badge — GearCard.js,
// NeedScreen.js's NeedItem, and ItemDetail.js all anchor one of these to
// their own badge rather than each reimplementing the same positioning math.
const HirablePopover = ({ anchorRef, festivalName, hireUrl, onClose }) => {
  const [position, setPosition] = useState(null);

  useEffect(() => {
    const anchorEl = anchorRef?.current;
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const spaceBelow = viewportHeight - rect.bottom;
    const placeAbove = spaceBelow < ESTIMATED_HEIGHT + GAP && rect.top > ESTIMATED_HEIGHT + GAP;

    const badgeCenterX = rect.left + rect.width / 2;
    let left = badgeCenterX - POPOVER_WIDTH / 2;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, viewportWidth - POPOVER_WIDTH - VIEWPORT_MARGIN));
    // Caret stays pointed at the actual badge centre even when the popover
    // itself has been clamped away from being perfectly centred on it.
    const caretLeft = Math.max(16, Math.min(badgeCenterX - left, POPOVER_WIDTH - 16));

    setPosition({
      left,
      top: placeAbove ? undefined : rect.bottom + GAP,
      bottom: placeAbove ? (viewportHeight - rect.top + GAP) : undefined,
      placeAbove,
      caretLeft,
    });
  }, [anchorRef]);

  // Dismiss on outside tap (skipping the anchor itself — its own onClick
  // already toggles this open/closed) and on Escape/scroll/resize, so a
  // stale-positioned popover never lingers after the page moves under it.
  useEffect(() => {
    const handleOutside = (e) => {
      if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
      onClose();
    };
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    const contentEl = document.querySelector('.content');
    document.addEventListener('mousedown', handleOutside, true);
    document.addEventListener('touchstart', handleOutside, true);
    document.addEventListener('keydown', handleKeyDown);
    contentEl && contentEl.addEventListener('scroll', onClose, { passive: true });
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', handleOutside, true);
      document.removeEventListener('touchstart', handleOutside, true);
      document.removeEventListener('keydown', handleKeyDown);
      contentEl && contentEl.removeEventListener('scroll', onClose);
      window.removeEventListener('resize', onClose);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!position) return null;

  return ReactDOM.createPortal(
    <div
      className={classes.popover}
      style={{
        left: position.left,
        top: position.top,
        bottom: position.bottom,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <span
        className={`${classes.caret} ${position.placeAbove ? classes.caretDown : classes.caretUp}`}
        style={{ left: position.caretLeft }}
        aria-hidden="true"
      />
      <p className={classes.heading}>Available to hire</p>
      <p className={classes.body}>
        You can hire this item at {festivalName || 'this event'} — no need to borrow or buy.
      </p>
      {hireUrl && (
        <a className={classes.link} href={hireUrl} target="_blank" rel="noreferrer">
          View hire options →
        </a>
      )}
    </div>,
    document.body
  );
};

export default HirablePopover;
